import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Lock, CreditCard, Truck } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCardForm from '../components/checkout/StripeCardForm';
import PaymentNoticeModal from '../components/checkout/PaymentNoticeModal';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { ordersAPI } from '../api/orders';
import { paymentsAPI } from '../api/payments';
import { useCurrencyStore } from '../store/currencyStore';
import { Address, ShippingMethod } from '../types';
import api from '../api/axios';
import toast from 'react-hot-toast';

type CheckoutStep = 'address' | 'shipping' | 'payment' | 'review';

const CheckoutPage: React.FC = () => {
  const [step, setStep] = useState<CheckoutStep>('address');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [selectedShipping, setSelectedShipping] = useState<string>('');
  const [nigeriaShippingType, setNigeriaShippingType] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [discountCode, setDiscountCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountMessage, setDiscountMessage] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_default: false,
  });
  const [showNewAddress, setShowNewAddress] = useState(false);

  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [stripeOrderId, setStripeOrderId] = useState<string | null>(null);
  const [isPreparingStripe, setIsPreparingStripe] = useState(false);
  const inputClass =
    'w-full bg-white border-2 border-secondary/40 focus:border-primary rounded-sm px-4 py-3 text-sm text-primary placeholder:text-secondary/70 placeholder:tracking-widest placeholder:text-xs outline-none transition-colors';
  const [paymentNotice, setPaymentNotice] = useState<{ title: string; message: string } | null>(null);
  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey]
  );

  const { cart, fetchCart } = useCartStore();
  const { selectedCurrency, format, exchangeRates } = useCurrencyStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Nigeria-made pieces ship completely differently (pickup / local Nigeria
  // delivery / WhatsApp-quoted international) from standard US-fulfilled
  // items. A cart can't mix the two — it doesn't make sense to "pick up in
  // Lagos" something that shipped from the US.
  const nigeriaItemCount = cart?.items?.filter((i: any) => i.is_nigeria_made).length || 0;
  const totalItemCount = cart?.items?.length || 0;
  const isAllNigeriaMade = nigeriaItemCount > 0 && nigeriaItemCount === totalItemCount;
  const isMixedOrigin = nigeriaItemCount > 0 && nigeriaItemCount < totalItemCount;

  useEffect(() => {
    fetchCart();
    fetchAddresses();
    fetchShippingMethods();
  }, []);

  const fetchAddresses = async () => {
    try {
      const res = await api.get('/api/addresses');
      if (res.data.success) {
        setAddresses(res.data.data);
        const defaultAddr = res.data.data.find((a: Address) => a.is_default);
        if (defaultAddr) setSelectedAddress(defaultAddr.id);
      }
    } catch {}
  };

  const fetchShippingMethods = async () => {
    try {
      const res = await api.get('/api/shipping');
      if (res.data.success) {
        setShippingMethods(res.data.data);
        if (res.data.data.length > 0) setSelectedShipping(res.data.data[0].id);
      }
    } catch {}
  };

  const handleAddNewAddress = async () => {
    try {
      const res = await api.post('/api/addresses', newAddress);
      if (res.data.success) {
        await fetchAddresses();
        setSelectedAddress(res.data.data.id);
        setShowNewAddress(false);
        toast.success('Address added');
      }
    } catch {
      toast.error('Failed to add address');
    }
  };

  const handleValidateDiscount = async () => {
    if (!discountCode) return;
    try {
      const res = await api.post('/api/discounts/validate', {
        code: discountCode,
        order_total: cart?.subtotal || 0,
      });
      if (res.data.success) {
        const data = res.data.data;
        if (data.valid) {
          setDiscountAmount(data.discount_amount);
          setDiscountMessage(data.message);
          toast.success('Discount applied');
        } else {
          setDiscountAmount(0);
          setDiscountMessage(data.message);
          toast.error(data.message);
        }
      }
    } catch {
      toast.error('Failed to validate discount');
    }
  };

  const getShippingCost = () => {
    if (isAllNigeriaMade) return 0;
    const method = shippingMethods.find((m) => m.id === selectedShipping);
    return method ? Number(method.price) : 0;
  };

  // Mirrors the backend's tax_rate_for_country — US sales tax and Nigeria
  // VAT, applied to product + shipping combined. This is just a live
  // preview; the actual charge is calculated authoritatively server-side.
  const getTaxRate = () => {
    const address = addresses.find((a) => a.id === selectedAddress);
    const country = (address?.country || '').trim().toLowerCase();
    if (!country) return 0;
    // Exact match for short codes (avoids "us" matching inside unrelated
    // words), substring match for full names/abbreviations.
    if (country === 'us' || country === 'u.s.' || country === 'u.s.a' || country.includes('united states') || country.includes('usa')) {
      return 0.0825;
    }
    if (country === 'ng' || country.includes('nigeria')) {
      return 0.075;
    }
    return 0;
  };

  const getTax = () => {
    return (Number(cart?.subtotal || 0) + getShippingCost()) * getTaxRate();
  };

  const getTotal = () => {
    const subtotal = cart?.subtotal || 0;
    const shipping = getShippingCost();
    const tax = getTax();
    return Number(subtotal) + shipping + tax - discountAmount;
  };

  const convertToChargeCurrency = (amountUsd: number, toCurrency: string) => {
    if (toCurrency === 'USD') return amountUsd;
    const rate = exchangeRates[toCurrency] || 1;
    return amountUsd * rate;
  };

  const isNgn = selectedCurrency.code === 'NGN';
  // Confirmed directly from Stripe's own "Invalid currency" error response —
  // this account has no GHS-settling bank account linked. Fall back to USD
  // for the actual Stripe charge; the customer still sees GHS everywhere else.
  const STRIPE_UNSUPPORTED_CURRENCIES = ['GHS'];
  const getStripeCurrency = () =>
    STRIPE_UNSUPPORTED_CURRENCIES.includes(selectedCurrency.code) ? 'USD' : selectedCurrency.code;

  const handleSelectCard = async () => {
    setPaymentMethod('card');

    if (isNgn || stripeClientSecret) return;

    // If a previous attempt already created the order but Stripe init
    // failed, reuse that order instead of creating a second one (which
    // would hit an already-emptied cart and fail with "Cart is empty").
    if (stripeOrderId) {
      try {
        setIsPreparingStripe(true);
        const stripeCurrency = getStripeCurrency();
        const amount = convertToChargeCurrency(getTotal(), stripeCurrency);
        const stripeRes = await paymentsAPI.initializeStripe({
          order_id: stripeOrderId,
          amount,
          currency: stripeCurrency,
        });
        if (stripeRes.success && stripeRes.data) {
          setStripeClientSecret(stripeRes.data.client_secret);
          setStripePublishableKey(stripeRes.data.publishable_key);
        } else {
          setPaymentNotice({ title: "Card Payment Unavailable", message: stripeRes.message || "We were unable to set up card payment right now. Please try again or choose a different payment method." });
        }
      } catch (err: any) {
        setPaymentNotice({ title: "Card Payment Unavailable", message: err.response?.data?.message || "We were unable to set up card payment right now. Please try again or choose a different payment method." });
      } finally {
        setIsPreparingStripe(false);
      }
      return;
    }

    if (!selectedAddress) {
      toast.error('Please select a delivery address first');
      setStep('address');
      return;
    }
    if (isAllNigeriaMade ? !nigeriaShippingType : !selectedShipping) {
      toast.error('Please select a shipping option first');
      setStep('shipping');
      return;
    }

    try {
      setIsPreparingStripe(true);
      const orderRes = await ordersAPI.createOrder({
        shipping_address_id: selectedAddress,
        ...(isAllNigeriaMade
          ? { shipping_type: nigeriaShippingType }
          : { shipping_method_id: selectedShipping }),
        payment_method: 'card',
        discount_code: discountCode || undefined,
        notes: notes || undefined,
      });

      if (!orderRes.success || !orderRes.data) {
        setPaymentNotice({ title: "Order Could Not Be Created", message: orderRes.message || "We were unable to create your order. Please check your details and try again." });
        return;
      }

      const orderId = orderRes.data.order_id;
      const total = orderRes.data.total;
      const stripeCurrency = getStripeCurrency();
      const amount = convertToChargeCurrency(total, stripeCurrency);

      // Set the order id immediately so a failed Stripe init can retry
      // against this same order instead of creating a new one.
      setStripeOrderId(orderId);

      const stripeRes = await paymentsAPI.initializeStripe({
        order_id: orderId,
        amount,
        currency: stripeCurrency,
      });

      if (stripeRes.success && stripeRes.data) {
        setStripeClientSecret(stripeRes.data.client_secret);
        setStripePublishableKey(stripeRes.data.publishable_key);
      } else {
        setPaymentNotice({ title: "Card Payment Unavailable", message: stripeRes.message || "We were unable to set up card payment right now. Please try again or choose a different payment method." });
      }
    } catch (err: any) {
      setPaymentNotice({ title: "Card Payment Unavailable", message: err.response?.data?.message || "We were unable to set up card payment right now. Please try again or choose a different payment method." });
    } finally {
      setIsPreparingStripe(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      toast.error('Please select a delivery address');
      return;
    }
    if (isAllNigeriaMade ? !nigeriaShippingType : !selectedShipping) {
      toast.error('Please select a shipping option');
      return;
    }
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }
    if (paymentMethod === 'paystack' && !isNgn) {
      setPaymentNotice({
        title: 'Paystack Not Available',
        message: `Paystack only processes payments in Nigerian Naira (NGN). Your checkout is currently set to ${selectedCurrency.code}. Please choose "Pay with Card" instead, or switch your currency to NGN if you'd like to pay via Paystack.`,
      });
      return;
    }

    try {
      setIsLoading(true);

      const orderData = {
        shipping_address_id: selectedAddress,
        ...(isAllNigeriaMade
          ? { shipping_type: nigeriaShippingType }
          : { shipping_method_id: selectedShipping }),
        payment_method: paymentMethod === 'card' ? 'paystack' : paymentMethod,
        discount_code: discountCode || undefined,
        notes: notes || undefined,
      };

      const orderRes = await ordersAPI.createOrder(orderData);

      if (!orderRes.success || !orderRes.data) {
        setPaymentNotice({ title: "Order Could Not Be Created", message: orderRes.message || "We were unable to create your order. Please check your details and try again." });
        return;
      }

      const orderId = orderRes.data.order_id;
      const total = orderRes.data.total;

      if (paymentMethod === 'card') {
        const paystackCurrency = 'NGN';
        const paystackAmount = convertToChargeCurrency(total, paystackCurrency);

        const paystackRes = await paymentsAPI.initializePaystack({
          order_id: orderId,
          email: user?.email || '',
          amount: paystackAmount,
          currency: paystackCurrency,
          card_only: true,
        });

        if (paystackRes.success && paystackRes.data?.authorization_url) {
          window.location.href = paystackRes.data.authorization_url;
        } else {
          setPaymentNotice({
            title: 'Payment Could Not Be Started',
            message: paystackRes.message || 'We were unable to start your card payment. Your order has been saved as pending — please try again from your order page.',
          });
        }
      } else if (paymentMethod === 'paystack') {
        const paystackCurrency = 'NGN';
        const paystackAmount = convertToChargeCurrency(total, paystackCurrency);

        const paystackRes = await paymentsAPI.initializePaystack({
          order_id: orderId,
          email: user?.email || '',
          amount: paystackAmount,
          currency: paystackCurrency,
        });

        if (paystackRes.success && paystackRes.data?.authorization_url) {
          window.location.href = paystackRes.data.authorization_url;
        } else {
          setPaymentNotice({
            title: 'Payment Could Not Be Started',
            message: paystackRes.message || 'We were unable to start your Paystack payment. Your order has been saved as pending — please try again from your order page.',
          });
        }
      } else if (paymentMethod === 'paypal') {
        const PAYPAL_SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'];
        const paypalCurrency = PAYPAL_SUPPORTED_CURRENCIES.includes(selectedCurrency.code)
          ? selectedCurrency.code
          : 'USD';
        const paypalAmount = convertToChargeCurrency(total, paypalCurrency);

        const paypalRes = await paymentsAPI.initializePaypal({
          order_id: orderId,
          amount: paypalAmount,
          currency: paypalCurrency,
        });

        if (paypalRes.success && paypalRes.data?.approve_url) {
          window.location.href = paypalRes.data.approve_url;
        } else {
          setPaymentNotice({
            title: 'Payment Could Not Be Started',
            message: paypalRes.message || 'We were unable to start your PayPal payment. Your order has been saved as pending — please try again from your order page.',
          });
        }
      }
    } catch (err: any) {
      console.error('Order error:', err);
      setPaymentNotice({
        title: 'Something Went Wrong',
        message: err.response?.data?.message || 'We were unable to process your order. Please check your details and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const steps: { id: CheckoutStep; label: string }[] = [
    { id: 'address', label: 'Address' },
    { id: 'shipping', label: 'Shipping' },
    { id: 'payment', label: 'Payment' },
    { id: 'review', label: 'Review' },
  ];

  const stepIndex = steps.findIndex((s) => s.id === step);
  const showingInlineStripe = paymentMethod === 'card' && !isNgn;

  if (!cart || cart.items.length === 0) {
    navigate('/shop');
    return null;
  }

  if (isMixedOrigin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl text-primary mb-4">
            Two Different Shipping Types in Your Bag
          </h1>
          <p className="text-sm text-secondary leading-relaxed mb-2">
            Your bag has both Nigeria-made pieces and standard items — these ship differently, so they can't be checked out together.
          </p>
          <p className="text-xs text-nude leading-relaxed mb-8">
            Please complete these as two separate orders: one for your Nigeria-made pieces, and one for everything else.
          </p>
          <button onClick={() => navigate('/shop')} className="btn-primary">
            Return to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-light py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="font-serif text-2xl tracking-widest uppercase text-primary">
            Lanre Savage
          </h1>
          <div className="flex items-center space-x-1 text-xs tracking-widest uppercase">
            <Lock size={12} className="text-nude" />
            <span className="text-nude">Secure Checkout</span>
          </div>
        </div>
      </div>

      <div className="border-b border-light py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-center space-x-8">
          {steps.map((s, index) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => index <= stepIndex && setStep(s.id)}
                className={`text-xs tracking-widest uppercase transition-colors ${
                  s.id === step
                    ? 'text-primary font-medium'
                    : index < stepIndex
                    ? 'text-nude hover:text-primary cursor-pointer'
                    : 'text-nude cursor-not-allowed'
                }`}
              >
                {s.label}
              </button>
              {index < steps.length - 1 && (
                <ChevronRight size={12} className="text-nude" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {step === 'address' && (
                <div>
                  <h2 className="font-serif text-2xl text-primary mb-8">
                    Shipping Address
                  </h2>

                  {addresses.length > 0 && (
                    <div className="space-y-3 mb-6">
                      {addresses.map((addr) => (
                        <label
                          key={addr.id}
                          className={`flex items-start space-x-4 p-4 border cursor-pointer transition-colors ${
                            selectedAddress === addr.id
                              ? 'border-primary'
                              : 'border-light hover:border-nude'
                          }`}
                        >
                          <input
                            type="radio"
                            name="address"
                            value={addr.id}
                            checked={selectedAddress === addr.id}
                            onChange={() => setSelectedAddress(addr.id)}
                            className="mt-1"
                          />
                          <div className="text-xs">
                            <p className="font-medium tracking-wide">
                              {addr.first_name} {addr.last_name}
                              {addr.is_default && (
                                <span className="ml-2 text-nude">(Default)</span>
                              )}
                            </p>
                            <p className="text-secondary mt-1 leading-relaxed">
                              {addr.address_line1}
                              {addr.address_line2 && `, ${addr.address_line2}`}
                              <br />
                              {addr.city}, {addr.state} {addr.postal_code}
                              <br />
                              {addr.country}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setShowNewAddress(!showNewAddress)}
                    className="text-xs tracking-widest uppercase text-primary hover:text-nude transition-colors mb-6"
                  >
                    + Add New Address
                  </button>

                  {showNewAddress && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-4 mb-6 overflow-hidden"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          placeholder="FIRST NAME"
                          value={newAddress.first_name}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            first_name: e.target.value
                          })}
                          className={inputClass}
                        />
                        <input
                          placeholder="LAST NAME"
                          value={newAddress.last_name}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            last_name: e.target.value
                          })}
                          className={inputClass}
                        />
                      </div>
                      <input
                        placeholder="ADDRESS LINE 1"
                        value={newAddress.address_line1}
                        onChange={(e) => setNewAddress({
                          ...newAddress,
                          address_line1: e.target.value
                        })}
                        className={inputClass}
                      />
                      <input
                        placeholder="ADDRESS LINE 2 (OPTIONAL)"
                        value={newAddress.address_line2}
                        onChange={(e) => setNewAddress({
                          ...newAddress,
                          address_line2: e.target.value
                        })}
                        className={inputClass}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          placeholder="CITY"
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            city: e.target.value
                          })}
                          className={inputClass}
                        />
                        <input
                          placeholder="STATE"
                          value={newAddress.state}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            state: e.target.value
                          })}
                          className={inputClass}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          placeholder="POSTAL CODE"
                          value={newAddress.postal_code}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            postal_code: e.target.value
                          })}
                          className={inputClass}
                        />
                        <select
                          value={newAddress.country}
                          onChange={(e) => setNewAddress({
                            ...newAddress,
                            country: e.target.value
                          })}
                          className={inputClass}
                        >
                          <option value="">SELECT COUNTRY</option>
                          <option value="Nigeria">Nigeria</option>
                          <option value="United States">United States</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="Canada">Canada</option>
                          <option value="Ghana">Ghana</option>
                          <option value="Kenya">Kenya</option>
                          <option value="South Africa">South Africa</option>
                          <option value="France">France</option>
                          <option value="Germany">Germany</option>
                          <option value="Australia">Australia</option>
                          <option value="United Arab Emirates">United Arab Emirates</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <button
                        onClick={handleAddNewAddress}
                        className="btn-secondary text-xs"
                      >
                        Save Address
                      </button>
                    </motion.div>
                  )}

                  <button
                    onClick={() => setStep('shipping')}
                    disabled={!selectedAddress}
                    className="btn-primary disabled:opacity-40"
                  >
                    Continue to Shipping
                  </button>
                </div>
              )}

              {step === 'shipping' && (
                <div>
                  <h2 className="font-serif text-2xl text-primary mb-8">
                    Shipping Method
                  </h2>

                  {isAllNigeriaMade ? (
                    <>
                      <div className="space-y-3 mb-8">
                        {[
                          {
                            key: 'pickup',
                            label: 'Pick Up In Store (Nigeria)',
                            desc: 'Free — collect your order in person.',
                            price: 'Free',
                          },
                          {
                            key: 'nigeria_local',
                            label: 'Deliver Locally (Nigeria)',
                            desc: 'Our team will confirm the delivery cost with you on WhatsApp based on your location.',
                            price: 'Quoted via WhatsApp',
                          },
                          {
                            key: 'nigeria_international',
                            label: 'Deliver Internationally',
                            desc: "Cost depends on the item's weight once packed — we'll message you on WhatsApp with the final shipping cost after your order is ready.",
                            price: 'Quoted via WhatsApp',
                          },
                        ].map((opt) => (
                          <label
                            key={opt.key}
                            className={`flex items-center justify-between p-4 border cursor-pointer transition-colors ${
                              nigeriaShippingType === opt.key
                                ? 'border-primary'
                                : 'border-light hover:border-nude'
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <input
                                type="radio"
                                name="nigeria-shipping"
                                value={opt.key}
                                checked={nigeriaShippingType === opt.key}
                                onChange={() => setNigeriaShippingType(opt.key)}
                              />
                              <div>
                                <p className="text-xs tracking-widest uppercase font-medium">
                                  {opt.label}
                                </p>
                                <p className="text-xs text-nude mt-1 max-w-md">
                                  {opt.desc}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs text-nude tracking-wide whitespace-nowrap ml-4">
                              {opt.price}
                            </span>
                          </label>
                        ))}
                      </div>

                      <div className="bg-light p-4 mb-8 flex items-start space-x-3">
                        <Truck size={16} className="text-nude mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-secondary leading-relaxed">
                          This piece is made in Nigeria, so it ships differently from our standard items. You won't be charged for shipping now — for local or international delivery, our team will reach out on WhatsApp with the exact cost once it's known.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-3 mb-8">
                        {shippingMethods.map((method) => (
                          <label
                            key={method.id}
                            className={`flex items-center justify-between p-4 border cursor-pointer transition-colors ${
                              selectedShipping === method.id
                                ? 'border-primary'
                                : 'border-light hover:border-nude'
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <input
                                type="radio"
                                name="shipping"
                                value={method.id}
                                checked={selectedShipping === method.id}
                                onChange={() => setSelectedShipping(method.id)}
                              />
                              <div>
                                <p className="text-xs tracking-widest uppercase font-medium">
                                  {method.name}
                                </p>
                                {method.estimated_days && (
                                  <p className="text-xs text-nude mt-1">
                                    {method.estimated_days}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="text-sm">
                              {Number(method.price) === 0
                                ? 'Free'
                                : `$${Number(method.price).toFixed(2)}`}
                            </span>
                          </label>
                        ))}
                        {shippingMethods.length === 0 && (
                          <p className="text-xs text-nude tracking-widest uppercase">
                            No shipping methods available
                          </p>
                        )}
                      </div>

                      <div className="bg-light p-4 mb-8 flex items-start space-x-3">
                        <Truck size={16} className="text-nude mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-secondary leading-relaxed">
                          Your order is packed and shipped via <strong>USPS/UPS through Pirate Ship</strong>, our shipping partner. The option you select above determines your estimated delivery window and cost — you'll receive a tracking number by email once your order ships.
                        </p>
                      </div>
                    </>
                  )}

                  <div className="flex space-x-4">
                    <button
                      onClick={() => setStep('address')}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setStep('payment')}
                      className="btn-primary"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              )}

              {step === 'payment' && (
                <div>
                  <h2 className="font-serif text-2xl text-primary mb-8">
                    Payment Method
                  </h2>
                  <div className="space-y-3 mb-8">
                    <button
                      onClick={handleSelectCard}
                      className={`w-full text-left p-4 border transition-colors ${
                        paymentMethod === 'card' ? 'border-primary' : 'border-light hover:border-nude'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CreditCard size={18} className="text-nude" />
                          <div>
                            <p className="text-xs font-medium tracking-widest uppercase">Pay with Card</p>
                            <p className="text-xs text-nude mt-1">Visa, Mastercard — enter your card details directly</p>
                          </div>
                        </div>
                        {paymentMethod === 'card' && <span className="text-primary text-xs">✓</span>}
                      </div>
                    </button>

                    {showingInlineStripe && (
                      <div>
                        {isPreparingStripe && (
                          <p className="text-xs text-nude px-1">Preparing secure card form...</p>
                        )}
                        {stripeClientSecret && stripePromise && stripeOrderId && (
                          <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                            <StripeCardForm
                              orderId={stripeOrderId}
                              totalLabel={format(getTotal())}
                              onSuccess={() => navigate(`/orders/${stripeOrderId}`)}
                            />
                          </Elements>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => {
                        if (!isNgn) {
                          setPaymentNotice({
                            title: 'Paystack Not Available',
                            message: `Paystack only processes payments in Nigerian Naira (NGN). Your checkout is currently set to ${selectedCurrency.code}. Please use "Pay with Card" instead, or switch your currency to NGN using the currency selector at the top of the site if you'd like to pay via Paystack.`,
                          });
                          return;
                        }
                        setPaymentMethod('paystack');
                      }}
                      className={`w-full text-left p-4 border transition-colors ${
                        paymentMethod === 'paystack' ? 'border-primary' : 'border-light hover:border-nude'
                      } ${!isNgn ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium tracking-widest uppercase">Paystack</p>
                          <p className="text-xs text-nude mt-1">Card, Bank Transfer, USSD — Nigeria only</p>
                          {isNgn ? (
                            <p className="text-xs text-green-600 mt-1">✓ Recommended for Nigerian customers</p>
                          ) : (
                            <p className="text-xs text-amber-600 mt-1">⚠ Requires NGN currency — tap to learn more</p>
                          )}
                        </div>
                        {paymentMethod === 'paystack' && <span className="text-primary text-xs">✓</span>}
                      </div>
                    </button>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xs tracking-widest uppercase mb-4">
                      Discount Code
                    </h3>
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className={`${inputClass} flex-1`}
                      />
                      <button
                        onClick={handleValidateDiscount}
                        className="btn-secondary text-xs px-6"
                      >
                        Apply
                      </button>
                    </div>
                    {discountMessage && (
                      <p className={`text-xs mt-2 ${
                        discountAmount > 0 ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {discountMessage}
                      </p>
                    )}
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xs tracking-widest uppercase mb-4">
                      Order Notes (Optional)
                    </h3>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Special instructions..."
                      rows={3}
                      className="w-full border border-light p-3 text-xs text-primary placeholder-nude outline-none focus:border-primary transition-colors resize-none"
                    />
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => setStep('shipping')}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    {!showingInlineStripe && (
                      <button
                        onClick={() => setStep('review')}
                        className="btn-primary"
                      >
                        Review Order
                      </button>
                    )}
                  </div>
                </div>
              )}

              {step === 'review' && (
                <div>
                  <h2 className="font-serif text-2xl text-primary mb-8">
                    Review Your Order
                  </h2>

                  <div className="space-y-4 mb-8">
                    {cart.items.map((item) => (
                      <div key={item.id} className="flex space-x-4">
                        <div className="w-16 h-20 bg-light flex-shrink-0">
                          {item.image_url && (
                            <img
                              src={item.image_url}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs tracking-wide font-medium">
                            {item.product_name}
                          </p>
                          <p className="text-xs text-nude mt-1">
                            {item.size && `Size: ${item.size}`}
                            {item.color && ` / ${item.color}`}
                          </p>
                          <p className="text-xs text-nude">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-sm">
                          {format(Number(item.price) * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-light pt-6 mb-8">
                    <p className="text-xs tracking-widest uppercase text-nude mb-2">
                      Payment: {paymentMethod.replace('_', ' ').toUpperCase()}
                    </p>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => setStep('payment')}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={isLoading}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {isLoading
                        ? 'Processing...'
                        : `Place Order — ${format(getTotal())}`}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-light p-6 sticky top-24">
              <h2 className="font-serif text-lg text-primary mb-6">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                {cart.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-xs">
                    <span className="text-secondary">
                      {item.product_name} × {item.quantity}
                    </span>
                    <span>${(Number(item.price) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-nude/30 pt-4 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-nude tracking-wide">Subtotal</span>
                  <span>{format(Number(cart.subtotal))}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-nude tracking-wide">Shipping</span>
                  <span>
  {step === 'address' ? (
    <span className="text-nude italic">Calculated at next step</span>
  ) : getShippingCost() === 0 ? (
    'Free'
  ) : (
    format(getShippingCost())
  )}
</span>
                </div>
                {getTax() > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-nude tracking-wide">
                      Tax ({(getTaxRate() * 100).toFixed(2)}%)
                    </span>
                    <span>{format(getTax())}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-xs text-green-600">
                    <span>Discount</span>
                    <span>-{format(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-medium pt-3 border-t border-nude/30">
                  <span className="tracking-widest uppercase">Total</span>
                 <span>{format(getTotal())}</span>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2 mt-6">
                <Lock size={10} className="text-nude" />
                <p className="text-xs text-nude tracking-wide">
                  Secure SSL Encrypted Payment
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {paymentNotice && (
        <PaymentNoticeModal
          title={paymentNotice.title}
          message={paymentNotice.message}
          onClose={() => setPaymentNotice(null)}
          primaryAction={
            paymentNotice.title === 'Paystack Not Available'
              ? { label: 'Use Card Instead', onClick: () => { setPaymentNotice(null); setStep('payment'); handleSelectCard(); } }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default CheckoutPage;